import React, { useContext, useEffect, useState, useCallback, useMemo, ChangeEvent, FormHTMLAttributes, FormEvent } from "react";
import { AuthContext } from "src/context/AuthContext";
import { ReportResponse, ClientReport, GridRow } from './types';
import LoadingOverlay from "src/components/Shared/LoadingOverlay/LoadingOverlay";
import { LGrid, LFormInput, LButton } from "@loffa/gpp-modules";
import styles from "./UserEntitlementReport.module.css";
import { ProductTypeEnum } from "src/enums/productTypeEnum";

const GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE;

export default function UserEntitlementReport() {
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [gridData, setGridData] = useState<GridRow[]>([]);
  const [editState, setEditState] = useState<any>({});
  const [reportDate, setReportDate] = useState<Date>(new Date());

  useEffect(() => {
    const fetchEntitlements = async () => {
      setLoading(true);
      try {
        const url = `${GATEWAY_BASE_URL}/multitenancy/api/Clients/EntitlementReport`;
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
            "Content-Type": "application/json",
          },
        });
        const data: ReportResponse = await res.json();
        const processedClients: ClientReport[] = data.data.map(client => ({
          ...client,
          productTypes: client.productTypes.map(pt => ({
            ...pt,
            maxIncludedUsers: pt.maxIncludedUsers || 0
          }))
        }));
        const transformedData = processReportForGrid(processedClients);
        setGridData(transformedData);
      } catch (error) {
        console.error("Error fetching entitlement data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEntitlements();
  }, [authContext]);

  const processReportForGrid = (clients: ClientReport[]): GridRow[] => {
    return clients.map((client, index) => {
      //active users
      const fvdSCount = getProductCount(client, ProductTypeEnum.FVD_S);
      const fvdRCount = getProductCount(client, ProductTypeEnum.FVD_R);
      const pbinSCount = getProductCount(client, ProductTypeEnum.PBIN_S);
      const pbinRCount = getProductCount(client, ProductTypeEnum.PBIN_R);
      const qbsSCount = getProductCount(client, ProductTypeEnum.QBS_S);
      const qbsRCount = getProductCount(client, ProductTypeEnum.QBS_R);

      //max entitled users
      const fvdSMax = getMaxIncludedUsers(client, ProductTypeEnum.FVD_S);
      const fvdRMax = getMaxIncludedUsers(client, ProductTypeEnum.FVD_R);
      const pbinSMax = getMaxIncludedUsers(client, ProductTypeEnum.PBIN_S);
      const pbinRMax = getMaxIncludedUsers(client, ProductTypeEnum.PBIN_R);
      const qbsSMax = getMaxIncludedUsers(client, ProductTypeEnum.QBS_S);
      const qbsRMax = getMaxIncludedUsers(client, ProductTypeEnum.QBS_R);

      const uniqueId = `${client.dtc || "none"}-${client.company}-${index}`;

      return {
        id: uniqueId,
        dtc: client.dtc || "",
        company: client.company,
        fvdSCount,
        fvdRCount,
        pbinSCount,
        pbinRCount,
        qbsSCount,
        qbsRCount,
        fvdSMax,
        fvdRMax,
        pbinSMax,
        pbinRMax,
        qbsSMax,
        qbsRMax,
        clientIndex: index,
        pbinSuite: "-",
        fvdSuite: "-",
        qbsSuite: "-",
      };
    });
  };

  const getProductCount = (client: ClientReport, productType: ProductTypeEnum): number | undefined => {
    const product = client.productTypes.find((pt) => pt.productTypeName === productType);
    return product ? product.count : undefined;
  };

  const getMaxIncludedUsers = (client: ClientReport, productType: ProductTypeEnum): number | undefined => {
    const product = client.productTypes.find((pt) => pt.productTypeName === productType);
    return product ? product.maxIncludedUsers : undefined;
  };

  const handleItemChange = useCallback((event: any) => {
    const { dataItem, field, value } = event;
    const clientIndex = dataItem.clientIndex;
    setGridData((prev) =>
      prev.map((item) => {
        if (item.clientIndex !== clientIndex) return item;
        const updatedRow = { ...item, [field]: value };
        if (field === "pbinSuite" && value > 0) {
          updatedRow.pbinSMax = value;
          updatedRow.pbinRMax = value;
        }
        if (field === "fvdSuite" && value > 0) {
          updatedRow.fvdSMax = value;
          updatedRow.fvdRMax = value;
        }
        if (field === "qbsSuite" && value > 0) {
          updatedRow.qbsSMax = value;
          updatedRow.qbsRMax = value;
        }
        return updatedRow;
      })
    );
  }, []);

  const handleEditChange = useCallback((event: any) => {
    setEditState(event.edit);
  }, []);

  const exportToPDF = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const { exportGridDataToPdf } = await import("./utils/buildEntitlementReportPDF");
    exportGridDataToPdf(gridData, reportDate.toLocaleDateString());
  }, [gridData, reportDate]);

  const columns = useMemo(() => [
    {
      field: "dtc",
      title: "DTC#",
      width: "100px",
      editable: true,
      headerClassName: "client-info-header",
    },
    {
      field: "company",
      title: "Company",
      width: "300px",
      editable: true,
      headerClassName: "client-info-header",
    },
    {
      title: "Product + Active Users",
      width: "300px",
      headerClassName: "entitlement-group-header",
      children: [
        {
          field: "fvdSCount",
          title: "FVD-S",
          width: "85px",
          editor: "numeric",
          editable: true,
          className: "entitlement-cell",
        },
        {
          field: "fvdRCount",
          title: "FVD-R",
          width: "85px",
          editor: "numeric",
          editable: true,
          className: "entitlement-cell",
        },
        {
          field: "pbinSCount",
          title: "PBIN-S",
          width: "85px",
          editor: "numeric",
          editable: true,
          className: "entitlement-cell",
        },
        {
          field: "pbinRCount",
          title: "PBIN-R",
          width: "85px",
          editor: "numeric",
          editable: true,
          className: "entitlement-cell",
        },
        {
          field: "qbsSCount",
          title: "QBS-S",
          width: "85px",
          editor: "numeric",
          editable: true,
          className: "entitlement-cell",
        },
        {
          field: "qbsRCount",
          title: "QBS-R",
          width: "85px",
          editor: "numeric",
          editable: true,
          className: "entitlement-cell",
        },
      ],
    },
    {
      title: "Product + Max Included Users",
      width: "300px",
      headerClassName: "max-users-group-header",
      children: [
        {
          field: "fvdSuite",
          title: "FVD-Suite",
          width: "90px",
          editable: true,
          editor: "numeric",
          className: "max-users-cell",
        },
        {
          field: "pbinSuite",
          title: "PBIN-Suite",
          width: "90px",
          editable: true,
          editor: "numeric",
          className: "max-users-cell",
        },
        {
          field: "qbsSuite",
          title: "QBS-Suite",
          width: "90px",
          editable: true,
          editor: "numeric",
          className: "max-users-cell",
        },
        {
          field: "fvdSMax",
          title: "FVD-S",
          width: "85px",
          editable: true,
          editor: "numeric",
          className: "max-users-cell",
        },
        {
          field: "fvdRMax",
          title: "FVD-R",
          width: "85px",
          editable: true,
          editor: "numeric",
          className: "max-users-cell",
        },
        {
          field: "pbinSMax",
          title: "PBIN-S",
          width: "85px",
          editable: true,
          editor: "numeric",
          className: "max-users-cell",
        },
        {
          field: "pbinRMax",
          title: "PBIN-R",
          width: "85px",
          editable: true,
          editor: "numeric",
          className: "max-users-cell",
        },
        {
          field: "qbsSMax",
          title: "QBS-S",
          width: "85px",
          editable: true,
          editor: "numeric",
          className: "max-users-cell",
        },
        {
          field: "qbsRMax",
          title: "QBS-R",
          width: "85px",
          editable: true,
          editor: "numeric",
          className: "max-users-cell",
        },
      ],
    },
  ], []);

  if (loading) {
    return  <div className={styles.loadingContainer}> <LoadingOverlay loadingText="Loading" /> </div>
  }

  if (gridData.length === 0) {
    return <div>No data available</div>;
  }

  return (
    <form className={styles.reportContainer} onSubmit={exportToPDF}>
      <div className={styles.reportHeader}>
        <h1>User Entitlement Report</h1>
        <LButton 
          type="submit" 
          themeColor="primary"
        >
          Export
        </LButton>
      </div>
      <div className={styles.dateSection}>
        <LFormInput
            labelName="Report Date"
            type="datePicker"
            labelAlign="top"
            datePicker={{
              value: reportDate, 
              onChange: (e: ChangeEvent<HTMLInputElement>) => setReportDate(new Date(e.target.value)),
              name: "reportDate",
              required: true,
            }}
          />
      </div>
      <p className={styles.instructions}>
        <span style={{ color: "red" }}>* </span>
        Click a table cell to edit the value
        <br />
        Adjust the Report Date input if needed, fill out the "Max Entitled Users" columns for each client, then export the report. 
      </p>
      <div className={styles.gridContainer} onBlur={() => setEditState({})}>
        <LGrid
          data={gridData}
          columns={columns}
          dataItemKey="id"
          editable={{ mode: "incell" }}
          edit={editState}
          onItemChange={handleItemChange}
          onEditChange={handleEditChange}
        />
      </div>
    </form>
  );
}